import React from 'react';
import { 
  ChevronIcon, 
  CHECKBOX_FILTER_OPTIONS,
  URL_FILTER_OPTIONS,
  CUSTOM_DATE_FILTER_OPTIONS,
} from '../../../shared/filterUtils';
import { KanbanFilterOption } from './FilterSection';

// Number comparison operators
const NUMBER_OPERATORS = [
  { id: 'eq', label: '=' },
  { id: 'neq', label: '≠' },
  { id: 'gt', label: '>' },
  { id: 'gte', label: '≥' },
  { id: 'lt', label: '<' },
  { id: 'lte', label: '≤' },
];

/**
 * CustomFieldFilterSection Component
 * 
 * Renders appropriate filter UI based on custom field type:
 * - select: Multi-select from field options
 * - checkbox: Checked/Unchecked toggle
 * - text: Text search input
 * - number: Operator + number input
 * - date: Date range options
 * - url: Has URL / No URL options
 */
const CustomFieldFilterSection = ({
  field,
  filterCriteria = {},
  expandedSections,
  toggleSection,
  onToggleValue,
  onSearchChange,
  onNumberChange,
}) => {
  const sectionId = `custom-${field.id}`;
  const isExpanded = expandedSections.has(sectionId);
  
  // Calculate filter count based on field type
  const getFilterCount = () => {
    if (!filterCriteria) return 0;
    if (filterCriteria.values?.length > 0) return filterCriteria.values.length;
    if (filterCriteria.search) return 1;
    if (filterCriteria.operator && filterCriteria.value !== undefined && filterCriteria.value !== '') return 1;
    return 0;
  };
  
  const filterCount = getFilterCount();
  
  // Render content based on field type
  const renderContent = () => {
    switch (field.type) {
      case 'select':
        return (field.options || []).map(option => (
          <KanbanFilterOption
            key={option.id}
            label={option.label}
            color={option.color}
            checked={filterCriteria.values?.includes(option.id)}
            onChange={() => onToggleValue(option.id)}
          />
        ));
        
      case 'checkbox':
        return CHECKBOX_FILTER_OPTIONS.map(option => (
          <KanbanFilterOption
            key={option.id}
            label={option.label}
            color={option.color}
            checked={filterCriteria.values?.includes(option.id)}
            onChange={() => onToggleValue(option.id)}
          />
        ));
        
      case 'text':
        return (
          <div className="kanban-filter-dropdown__text-filter">
            <input
              type="text"
              placeholder="Contains..."
              value={filterCriteria.search || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              className="kanban-filter-dropdown__text-input"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
        
      case 'number':
        return (
          <div className="kanban-filter-dropdown__number-filter">
            <select
              value={filterCriteria.operator || 'eq'}
              onChange={(e) => onNumberChange(e.target.value, filterCriteria.value ?? '')}
              className="kanban-filter-dropdown__number-operator"
              onClick={(e) => e.stopPropagation()}
            >
              {NUMBER_OPERATORS.map(op => (
                <option key={op.id} value={op.id}>{op.label}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Value"
              value={filterCriteria.value ?? ''}
              onChange={(e) => onNumberChange(filterCriteria.operator || 'eq', e.target.value)}
              className="kanban-filter-dropdown__number-input"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        );
        
      case 'date':
        return CUSTOM_DATE_FILTER_OPTIONS.map(option => (
          <KanbanFilterOption
            key={option.id}
            label={option.label}
            color={option.color}
            checked={filterCriteria.values?.includes(option.id)}
            onChange={() => onToggleValue(option.id)}
          />
        ));
        
      case 'url':
        return URL_FILTER_OPTIONS.map(option => (
          <KanbanFilterOption
            key={option.id}
            label={option.label}
            color={option.color}
            checked={filterCriteria.values?.includes(option.id)}
            onChange={() => onToggleValue(option.id)}
          />
        ));
        
      default:
        return null;
    }
  };
  
  return (
    <div className="kanban-filter-dropdown__section">
      <button 
        className="kanban-filter-dropdown__section-header" 
        onClick={() => toggleSection(sectionId)}
      >
        <span className="kanban-filter-dropdown__section-title">{field.name}</span>
        {filterCount > 0 && (
          <span className="kanban-filter-dropdown__section-count">{filterCount}</span>
        )}
        <span className={`kanban-filter-dropdown__section-chevron ${isExpanded ? 'kanban-filter-dropdown__section-chevron--open' : ''}`}>
          <ChevronIcon />
        </span>
      </button>
      {isExpanded && (
        <div className="kanban-filter-dropdown__options">
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default CustomFieldFilterSection;
